import clsx from "clsx";
import { useEffect } from "react";
import { createPortal } from "react-dom";

import { Button as ButtonV2 } from "../../../../Button";
import {
  StepUpError,
  type StepUpPhase,
  StepUpRedirect,
  StepUpVerifying,
  useStepUpChallenge,
} from "../../StepUpFlow";
import { useStepUpFlowTranslations } from "../../useStepUpFlowTranslations";
import { WizardFooter } from "../WizardFooter";

type VerifyStepViewProps = {
  phase: StepUpPhase;
  onBack: () => void;
  onClose: () => void;
  onPhaseChange?: (phase: StepUpPhase) => void;
  isMobile?: boolean;
  footerContainer?: Element | null;
};

type VerifyStepProps = Omit<VerifyStepViewProps, "phase"> & {
  phase?: StepUpPhase;
  onVerified?: () => void;
  simulateFailure?: boolean;
  paused?: boolean;
};

export const VerifyStep = ({
  phase,
  onVerified,
  simulateFailure,
  paused,
  ...viewProps
}: VerifyStepProps) =>
  phase ? (
    <VerifyStepView phase={phase} {...viewProps} />
  ) : (
    <SelfDrivenVerifyStep
      onVerified={onVerified}
      simulateFailure={simulateFailure}
      paused={paused}
      {...viewProps}
    />
  );

const SelfDrivenVerifyStep = ({
  onVerified,
  simulateFailure,
  paused,
  ...viewProps
}: Omit<VerifyStepProps, "phase">) => {
  const { phase } = useStepUpChallenge({
    onVerified: onVerified ?? noop,
    simulateFailure,
    paused,
  });
  return <VerifyStepView phase={phase} {...viewProps} />;
};

const noop = () => {};

const VerifyStepView = ({
  phase,
  onBack,
  onClose,
  onPhaseChange,
  isMobile,
  footerContainer,
}: VerifyStepViewProps) => {
  const translations = useStepUpFlowTranslations();

  useEffect(() => {
    onPhaseChange?.(phase);
  }, [phase, onPhaseChange]);

  const footer = (
    <WizardFooter isMobile={isMobile}>
      {phase === "error" ? (
        <>
          <ButtonV2
            variant="tonal"
            size="md"
            onClick={onClose}
            data-testid="create_program_wizard_verify_close_button"
          >
            {translations.close}
          </ButtonV2>
          <ButtonV2
            variant="brand"
            size="md"
            onClick={onBack}
            data-testid="create_program_wizard_verify_retry_button"
          >
            {translations.retry}
          </ButtonV2>
        </>
      ) : (
        <ButtonV2
          variant="tonal"
          size="md"
          onClick={onBack}
          data-testid="create_program_wizard_verify_back_button"
        >
          {translations.back}
        </ButtonV2>
      )}
    </WizardFooter>
  );

  return (
    <div className={clsx("flex min-h-0 flex-1 flex-col", !isMobile && "gap-8")}>
      {phase === "redirect" && <StepUpRedirect isMobile={isMobile} />}
      {phase === "verifying" && (
        <StepUpVerifying isMobile={isMobile} action="create" />
      )}
      {phase === "error" && <StepUpError isMobile={isMobile} />}
      {footerContainer ? createPortal(footer, footerContainer) : footer}
    </div>
  );
};
